import React, { useState, useRef, useEffect, useCallback, ReactNode } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
  threshold?: number; // Distance to trigger refresh (default: 80px)
  maxPullDistance?: number; // Maximum pull distance (default: 120px)
  className?: string;
  disabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  threshold = 80,
  maxPullDistance = 120,
  className = '',
  disabled = false
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  // Haptic feedback for mobile devices
  const triggerHapticFeedback = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // Short vibration
    }
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled || isRefreshing) return;
    
    // Check if we're at the top of the scrollable content
    const container = containerRef.current;
    if (!container) return;
    
    const scrollTop = container.scrollTop || 0;
    const scrollLeft = container.scrollLeft || 0;
    
    // Only allow pull when at the very top and not scrolled horizontally
    if (scrollTop > 5 || scrollLeft > 5) return;

    console.log('🖱️ Touch start detected - pull-to-refresh enabled');
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    isDragging.current = true;
  }, [disabled, isRefreshing]);

  // Handle touch move
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging.current || disabled || isRefreshing) return;

    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;

    if (deltaY > 0) {
      e.preventDefault(); // Prevent default scroll
      const distance = Math.min(deltaY * 0.6, maxPullDistance); // Damping effect
      setPullDistance(distance);
      setIsPulling(distance > 10);
      
      if (distance > 10) {
        console.log(`📏 Pulling: ${Math.round(distance)}px / ${threshold}px threshold`);
      }
    }
  }, [disabled, isRefreshing, maxPullDistance, threshold]);

  // Handle touch end
  const handleTouchEnd = useCallback(async () => {
    if (!isDragging.current || disabled || isRefreshing) return;

    isDragging.current = false;
    
    if (pullDistance >= threshold) {
      // Trigger refresh
      triggerHapticFeedback();
      setIsRefreshing(true);
      setPullDistance(0);
      setIsPulling(false);
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
      }
    } else {
      // Reset without refresh
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [pullDistance, threshold, disabled, isRefreshing, onRefresh, triggerHapticFeedback]);

  // Add event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate rotation for the refresh indicator
  const rotation = Math.min((pullDistance / threshold) * 180, 180);
  const opacity = Math.min(pullDistance / threshold, 1);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        transform: isPulling ? `translateY(${pullDistance}px)` : 'translateY(0)',
        transition: isPulling ? 'none' : 'transform 0.3s ease-out'
      }}
    >
      {/* Pull-to-refresh indicator */}
      <div 
        className={`absolute top-0 left-0 right-0 flex items-center justify-center transition-opacity duration-200 ${
          isPulling || isRefreshing ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          height: `${Math.max(pullDistance, 60)}px`,
          opacity: opacity
        }}
      >
        <div className="flex flex-col items-center space-y-2">
          {/* Spinning refresh icon */}
          <div 
            className={`w-6 h-6 border-2 border-[#ffd700] border-t-transparent rounded-full ${
              isRefreshing ? 'animate-spin' : ''
            }`}
            style={{
              transform: isRefreshing ? 'rotate(0deg)' : `rotate(${rotation}deg)`,
              transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
            }}
          />
          
          {/* Status text */}
          <span className="text-sm text-[#666] font-medium">
            {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {children}
      </div>

      {/* Loading overlay during refresh */}
      {isRefreshing && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="flex flex-col items-center space-y-2">
            <div className="w-8 h-8 border-2 border-[#ffd700] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-[#666] font-medium">Updating...</span>
          </div>
        </div>
      )}
    </div>
  );
};
